/**
 * EntryList Component
 *
 * Groups entries by type with section headers.
 * Displays entries sorted chronologically within each section.
 */

import { EntryCard, AnyEntry, EntryType } from './EntryCard';

// Combined entry with type information
export interface TypedEntry {
	entry: AnyEntry;
	type: EntryType;
}

interface EntryListProps {
	entries: TypedEntry[];
	date: string;
	onEdit: (entry: AnyEntry, type: EntryType) => void;
	onDelete: (entry: AnyEntry, type: EntryType) => void;
	canModify: boolean;
	loading: boolean;
}

// Section definitions with order and labels
const sections: { type: EntryType | EntryType[]; label: string; icon: string }[] = [
	{ type: 'weight', label: 'Berat Badan', icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3' },
	{ type: 'meal', label: 'Makanan', icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6' },
	{ type: 'exercise', label: 'Olahraga', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
	{ type: 'health', label: 'Kesehatan', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
	{ type: ['visit', 'prescription'], label: 'Medis', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
	{ type: 'activity', label: 'Aktivitas', icon: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
];

/**
 * Get sortable time value from entry
 */
function getEntryTime(entry: AnyEntry, type: EntryType): number {
	// Use createdAt as the primary sort key
	return new Date(entry.createdAt).getTime();
}

/**
 * Format date for display in Indonesian
 */
function formatDateIndonesian(dateStr: string): string {
	if (!dateStr) return '';
	const date = new Date(dateStr);
	return date.toLocaleDateString('id-ID', {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

export function EntryList({
	entries,
	date,
	onEdit,
	onDelete,
	canModify,
	loading,
}: EntryListProps) {
	// Group entries by type
	const groupedEntries = entries.reduce<Record<EntryType, TypedEntry[]>>(
		(acc, item) => {
			if (!acc[item.type]) {
				acc[item.type] = [];
			}
			acc[item.type].push(item);
			return acc;
		},
		{} as Record<EntryType, TypedEntry[]>
	);

	// Sort entries within each group by time
	Object.keys(groupedEntries).forEach((type) => {
		groupedEntries[type as EntryType].sort((a, b) => {
			return getEntryTime(a.entry, a.type) - getEntryTime(b.entry, b.type);
		});
	});

	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
				<span className="ml-3 text-gray-600">Memuat data...</span>
			</div>
		);
	}

	const hasEntries = entries.length > 0;

	if (!hasEntries) {
		return (
			<div className="text-center py-12">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					className="h-12 w-12 mx-auto text-gray-300"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1}
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
					/>
				</svg>
				<h3 className="mt-4 text-lg font-medium text-gray-900">
					Tidak ada catatan
				</h3>
				<p className="mt-1 text-gray-500">
					Belum ada data tercatat untuk {formatDateIndonesian(date)}
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{sections.map((section) => {
				// Get entries for this section
				const sectionTypes = Array.isArray(section.type)
					? section.type
					: [section.type];
				const sectionEntries = sectionTypes.flatMap(
					(type) => groupedEntries[type] || []
				);

				if (sectionEntries.length === 0) return null;

				return (
					<div key={section.label} className="space-y-3">
						{/* Section Header */}
						<div className="flex items-center gap-2 pb-2 border-b border-gray-200">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-5 w-5 text-gray-500"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d={section.icon}
								/>
							</svg>
							<h3 className="text-lg font-semibold text-gray-900">
								{section.label}
							</h3>
							<span className="text-sm text-gray-500">
								({sectionEntries.length})
							</span>
						</div>

						{/* Entry Cards */}
						<div className="space-y-3">
							{sectionEntries.map((item) => (
								<EntryCard
									key={`${item.type}-${item.entry.id}`}
									entry={item.entry}
									entryType={item.type}
									onEdit={onEdit}
									onDelete={onDelete}
									canModify={canModify}
								/>
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
}
